import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs';
import { ApiControllers } from '../constants/api-controllers.constants';
import { CategoryDto, CategoryService } from './category.service';
import { EntityApiService } from './entity-api.service';
import { StateDropdownDto, StateService } from './state.service';
import {
  createPagedListRequest,
  normalizePagedList,
} from '../utils/paged-list.util';
import { ItemDto, PurityDto, SupplierDto } from '../models/dtos';
import { DropdownOption } from '../../shared/custom-select/custom-select.component';
import { formatPartyDisplayLabel } from '../utils/party-code.util';

export type MasterDataCacheKey =
  | 'suppliers'
  | 'items'
  | 'raw-items'
  | 'purities'
  | 'categories'
  | 'states';

/** Session TTL for master dropdown/list caches (ms). */
const CACHE_TTL_MS = 10 * 60 * 1000;

interface CacheEntry<T> {
  expiresAt: number;
  value: T;
}

@Injectable({ providedIn: 'root' })
export class MasterDataCacheService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly entityApi = inject(EntityApiService);
  private readonly categoryService = inject(CategoryService);
  private readonly stateService = inject(StateService);

  private readonly memory = new Map<MasterDataCacheKey, CacheEntry<unknown>>();
  private readonly inflight = new Map<MasterDataCacheKey, Observable<unknown>>();

  private readonly supplierApi = this.entityApi.crud<SupplierDto>(ApiControllers.Supplier);
  private readonly itemApi = this.entityApi.crud<ItemDto>(ApiControllers.Item);
  private readonly purityApi = this.entityApi.crud<PurityDto>(ApiControllers.Purity);

  /** Clear all cached master data (call on logout). */
  clearAll(): void {
    this.memory.clear();
    this.inflight.clear();
  }

  invalidate(key: MasterDataCacheKey): void {
    this.memory.delete(key);
    this.inflight.delete(key);
  }

  /**
   * Background warmup for frequent dropdowns — safe to call after layout loads.
   * Does not block UI; failures are ignored.
   */
  warmupFrequent(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const keys: MasterDataCacheKey[] = [
      'suppliers',
      'items',
      'purities',
      'categories',
      'states',
    ];

    for (const key of keys) {
      this.getRaw(key).pipe(catchError(() => of([]))).subscribe();
    }
  }

  getSupplierOptions(): Observable<DropdownOption[]> {
    return this.getSuppliers().pipe(
      map((rows) =>
        rows.map((s) => ({
          value: s.supplierId!,
          label: formatPartyDisplayLabel(s.firstName, s.lastName, s.supplierCode),
        })),
      ),
    );
  }

  getItemOptions(): Observable<DropdownOption[]> {
    return this.getItems().pipe(
      map((rows) =>
        rows.map((i) => ({
          value: i.itemId!,
          label: `${i.itemCode ?? ''} - ${i.item}`.replace(/^ - /, ''),
        })),
      ),
    );
  }

  getPurityOptions(): Observable<DropdownOption[]> {
    return this.getPurities().pipe(
      map((rows) =>
        rows.map((p) => ({
          value: p.purityId!,
          label: p.purity,
        })),
      ),
    );
  }

  getSuppliers(): Observable<SupplierDto[]> {
    return this.getRaw<SupplierDto[]>('suppliers');
  }

  getItems(): Observable<ItemDto[]> {
    return this.getRaw<ItemDto[]>('items');
  }

  /** Items flagged IsRawItem in master (for purchase raw mode). */
  getRawPurchaseItems(): Observable<ItemDto[]> {
    return this.getRaw<ItemDto[]>('raw-items');
  }

  getRawPurchaseItemOptions(): Observable<DropdownOption[]> {
    return this.getRawPurchaseItems().pipe(
      map((rows) =>
        rows.map((i) => ({
          value: i.itemId!,
          label: i.itemCode?.trim()
            ? `${i.item.trim()} (${i.itemCode.trim()})`
            : i.item.trim(),
        })),
      ),
    );
  }

  getPurities(): Observable<PurityDto[]> {
    return this.getRaw<PurityDto[]>('purities');
  }

  getCategories(): Observable<CategoryDto[]> {
    return this.getRaw<CategoryDto[]>('categories');
  }

  getStates(): Observable<StateDropdownDto[]> {
    return this.getRaw<StateDropdownDto[]>('states');
  }

  private getRaw<T>(key: MasterDataCacheKey): Observable<T> {
    if (!isPlatformBrowser(this.platformId)) {
      return of([] as T);
    }

    const hit = this.memory.get(key);
    if (hit && hit.expiresAt > Date.now()) {
      return of(hit.value as T);
    }

    const pending = this.inflight.get(key);
    if (pending) {
      return pending as Observable<T>;
    }

    const request$ = this.fetch(key).pipe(
      map((value) => value as T),
      tap((value) => {
        this.memory.set(key, {
          value,
          expiresAt: Date.now() + CACHE_TTL_MS,
        });
        this.inflight.delete(key);
      }),
      catchError((err) => {
        this.inflight.delete(key);
        return throwError(() => err);
      }),
      shareReplay(1),
    );

    this.inflight.set(key, request$ as Observable<unknown>);
    return request$;
  }

  private fetch(key: MasterDataCacheKey): Observable<unknown> {
    switch (key) {
      case 'suppliers':
        return this.supplierApi
          .getList(this.allRowsRequest('FirstName'))
          .pipe(map((res) => normalizePagedList(res.data).items));
      case 'items':
        return this.itemApi
          .getList(this.allRowsRequest('Item'))
          .pipe(map((res) => normalizePagedList(res.data).items));
      case 'raw-items':
        return this.itemApi
          .getList({
            ...this.allRowsRequest('Item'),
            rawItemOnly: true,
          } as Parameters<typeof this.itemApi.getList>[0])
          .pipe(map((res) => normalizePagedList(res.data).items));
      case 'purities':
        return this.purityApi
          .getList(this.allRowsRequest('Purity'))
          .pipe(map((res) => normalizePagedList(res.data).items));
      case 'categories':
        return this.categoryService
          .getCategories()
          .pipe(map((res) => (res.success && res.data ? res.data : [])));
      case 'states':
        return this.stateService
          .getDropdown()
          .pipe(map((res) => (res.success && res.data ? res.data : [])));
      default:
        return of([]);
    }
  }

  private allRowsRequest(sortColumn: string) {
    return createPagedListRequest({
      pageNumber: 1,
      pageSize: -1,
      sortColumn,
      sortDirection: 'ASC',
    });
  }
}
